"use client";

import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";

const MAX_LENGTH = 1000;
const PANEL_ID = "wtt-feedback-panel";

interface FeedbackWidgetProps {
  title: string;
  description: string;
  placeholder: string;
  emailLabel: string;
  emailPlaceholder: string;
  sendLabel: string;
  sendingLabel: string;
  successMessage: string;
  rateLimitMessage: string;
  errorMessage: string;
  closeAriaLabel: string;
  feedbackAriaLabel: string;
}

type PanelState = "form" | "success" | "rate-limit" | "error";

export function FeedbackWidget({
  title,
  description,
  placeholder,
  emailLabel,
  emailPlaceholder,
  sendLabel,
  sendingLabel,
  successMessage,
  rateLimitMessage,
  errorMessage,
  closeAriaLabel,
  feedbackAriaLabel,
}: FeedbackWidgetProps) {
  const [text, setText] = useState("");
  const [email, setEmail] = useState("");
  const [panelState, setPanelState] = useState<PanelState>("form");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const onToggle = (e: Event) => {
      if ((e as ToggleEvent).newState === "closed" && panelState !== "form") {
        setPanelState("form");
        setText("");
        setEmail("");
      }
    };
    panel.addEventListener("toggle", onToggle);
    return () => panel.removeEventListener("toggle", onToggle);
  }, [panelState]);

  useEffect(() => {
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      if (y > lastY && y > 100) {
        setIsVisible(false);
        panelRef.current?.hidePopover();
      } else if (y < lastY) {
        setIsVisible(true);
      }
      lastY = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const res = await fetch("/api/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          feedback: text.trim(),
          email: email.trim() || undefined,
          pageUrl: window.location.href,
        }),
      });

      if (res.status === 429) {
        setPanelState("rate-limit");
      } else if (res.ok) {
        setPanelState("success");
        setTimeout(() => panelRef.current?.hidePopover(), 2000);
      } else {
        setPanelState("error");
      }
    } catch {
      setPanelState("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const statusMessages: Record<Exclude<PanelState, "form">, string> = {
    success: successMessage,
    "rate-limit": rateLimitMessage,
    error: errorMessage,
  };

  return (
    <>
      <button
        type="button"
        popoverTarget={PANEL_ID}
        aria-label={feedbackAriaLabel}
        className={`fixed bottom-5 end-5 z-50 flex h-12 w-12 items-center justify-center rounded-full border-none bg-primary text-primary-foreground shadow-lg transition-all duration-300 hover:scale-110 ${
          isVisible
            ? "translate-y-0 opacity-100"
            : "pointer-events-none translate-y-20 opacity-0"
        }`}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      </button>

      <div
        ref={panelRef}
        id={PANEL_ID}
        popover="auto"
        className="bottom-20 end-5 top-auto left-auto m-0 w-80 max-w-[calc(100vw-40px)] rounded-xl border border-border bg-card p-4 font-[inherit] text-card-foreground shadow-xl"
      >
        <button
          type="button"
          popoverTarget={PANEL_ID}
          popoverTargetAction="hide"
          aria-label={closeAriaLabel}
          className="absolute end-2 top-2 border-none bg-transparent p-1 text-lg leading-none text-muted-foreground hover:text-foreground"
        >
          &times;
        </button>

        {panelState === "form" ? (
          <form onSubmit={handleSubmit}>
            <h3 className="mb-1 text-base font-semibold">{title}</h3>
            <p className="mb-3 text-sm text-muted-foreground">{description}</p>
            <textarea
              required
              placeholder={placeholder}
              maxLength={MAX_LENGTH}
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="w-full resize-y rounded-md border border-border bg-background p-2 font-[inherit] text-sm text-foreground focus:outline-2 focus:outline-ring"
              style={{ minHeight: 80, maxHeight: 200 }}
            />
            <output className="mt-1 block text-end text-xs text-muted-foreground">
              {text.length} / {MAX_LENGTH}
            </output>
            <label htmlFor="wtt-fb-email" className="mt-2.5 block text-[13px] text-muted-foreground">
              {emailLabel}
            </label>
            <input
              id="wtt-fb-email"
              type="email"
              placeholder={emailPlaceholder}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full rounded-md border border-border bg-background p-2 font-[inherit] text-sm text-foreground focus:outline-2 focus:outline-ring"
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="mt-3 w-full rounded-md bg-primary p-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? sendingLabel : sendLabel}
            </button>
          </form>
        ) : (
          <div className="py-6 text-center text-sm">
            {statusMessages[panelState]}
          </div>
        )}
      </div>
    </>
  );
}
